import {
    MonthlySubmitRequestDto,
    MonthlySubmitResponseDto,
    PatchTimelogDto,
    ProjectHoursSummaryWithPercentageDto,
    SubmissionIntervalPostDto,
    SubmitTimelogByOwnerDto,
    SubmitTimelogDto,
    TimeLogCreateDto,
    TimeLogDailySum,
    TimeLogDto,
    TimeLogPostDto,
    TimeLogSummaryHoursDto,
} from '../commonTypes/timelog.types';
import { diContainer } from '../libs/container';
import { ApiBaseClass, IServiceHttp } from './ApiBase';

export namespace TimeLogApiRequest {
    export interface CreateTimeLog {
        body: TimeLogCreateDto;
    }

    export interface SubmitTimelog {
        body: SubmitTimelogDto;
    }

    export interface GetSubmissionIntervals {
        query: {
            userSub: string;
            projectId: string;
            startDate: string;
            endDate: string;
        };
    }

    export interface GetSubmissionIntervalsForMonth {
        query: {
            userSub: string;
            projectId: string;
            month: string;
            year: number;
        };
    }

    export interface DeleteSubmissionInterval {
        params: {
            id: string;
        };
    }

    export interface DeleteSubmissionIntervalForMonth {
        query: {
            id: string;
            month: string;
            year: number;
        };
    }

    export interface GetUserTimelog {
        body: {
            from: string;
            to: string;
            projectId: string;
            userSub: string;
        };
    }

    export interface GetTimelogsByProject {
        body: {
            from: string;
            to: string;
            projectId: string;
            userSub?: string;
        };
    }

    export interface DeleteUserTimeLog {
        params: {
            id: string;
        };
    }

    export interface PatchTimelog {
        body: PatchTimelogDto;
    }

    export interface SubmitTimelogByOwner {
        body: SubmitTimelogByOwnerDto;
    }

    export interface MonthlySubmit {
        body: MonthlySubmitRequestDto;
    }

    export interface GetDailySumByProject {
        body: {
            projectId: string;
            from: string;
            to: string;
        };
    }

    export interface GetProjectHoursSummary {
        query: {
            projectId: string;
        };
    }

    export interface GetUserTimeLogs {
        query: {
            month: string;
            year: number;
            projectId: string;
        };
    }
}

export namespace TimeLogApiResponse {
    export interface TimeLogPost {
        body: TimeLogPostDto;
    }

    export interface SubmitTimeLog {
        body: SubmissionIntervalPostDto;
    }

    export interface GetSubmissionIntervals {
        body: any[];
    }

    export interface getTimelog {
        body: TimeLogDto[];
    }

    export interface PatchTimeLog {
        body: TimeLogDto;
    }

    export interface GetDailySumByProject {
        body: TimeLogDailySum[];
    }

    export interface GetProjectHoursSummary {
        body: ProjectHoursSummaryWithPercentageDto;
    }

    export interface GetUserTimeLogs {
        body: TimeLogSummaryHoursDto;
    }

    export interface MonthlySubmit {
        body: MonthlySubmitResponseDto;
    }
}

export class TimeLogApi {
    constructor(private readonly apiBase: ApiBaseClass) {}

    public create(rest: IServiceHttp<TimeLogApiRequest.CreateTimeLog, TimeLogApiResponse.TimeLogPost>) {
        return this.apiBase.configure({
            ...rest,
            method: 'POST',
            url: 'tml/v1/time-log',
        });
    }

    public submitTimelog(rest: IServiceHttp<TimeLogApiRequest.SubmitTimelog, TimeLogApiResponse.SubmitTimeLog>) {
        return this.apiBase.configure({
            ...rest,
            method: 'POST',
            url: 'tml/v1/submission-interval/for-month',
        });
    }

    public getSubmissionIntervals(rest: IServiceHttp<TimeLogApiRequest.GetSubmissionIntervals, TimeLogApiResponse.GetSubmissionIntervals>) {
        return this.apiBase.configure({
            ...rest,
            method: 'GET',
            url: 'tml/v1/submission-interval',
        });
    }

    public getSubmissionIntervalsForMonth(
        rest: IServiceHttp<TimeLogApiRequest.GetSubmissionIntervalsForMonth, TimeLogApiResponse.GetSubmissionIntervals>
    ) {
        return this.apiBase.configure({
            ...rest,
            method: 'GET',
            url: 'tml/v1/submission-interval/for-month',
        });
    }

    public deleteSubmissionInterval(rest: IServiceHttp<TimeLogApiRequest.DeleteSubmissionInterval, void>) {
        return this.apiBase.configure({
            ...rest,
            method: 'DELETE',
            url: 'tml/v1/submission-interval/:id',
        });
    }

    public deleteSubmissionIntervalForMonth(rest: IServiceHttp<TimeLogApiRequest.DeleteSubmissionIntervalForMonth, void>) {
        return this.apiBase.configure({
            ...rest,
            method: 'DELETE',
            url: 'tml/v1/submission-interval/for-month',
        });
    }

    public getUserTimelog(rest: IServiceHttp<TimeLogApiRequest.GetUserTimelog, TimeLogApiResponse.getTimelog>) {
        return this.apiBase.configure({
            ...rest,
            method: 'GET',
            url: 'tml/v1/time-log.byProjectOfUser',
        });
    }

    public getTimelogsByProject(rest: IServiceHttp<TimeLogApiRequest.GetTimelogsByProject, TimeLogApiResponse.getTimelog>) {
        return this.apiBase.configure({
            ...rest,
            method: 'GET',
            url: 'tml/v1/time-log/-byProject',
        });
    }

    public deleteUserTimelog(rest: IServiceHttp<TimeLogApiRequest.DeleteUserTimeLog, void>) {
        return this.apiBase.configure({
            ...rest,
            method: 'DELETE',
            url: 'tml/v1/time-log/:id',
        });
    }

    public patchTimelog(rest: IServiceHttp<TimeLogApiRequest.PatchTimelog, TimeLogApiResponse.PatchTimeLog>) {
        return this.apiBase.configure({
            ...rest,
            method: 'PATCH',
            url: 'tml/v1/time-log/:id',
        });
    }

    public submitTimelogByOwner(rest: IServiceHttp<TimeLogApiRequest.SubmitTimelogByOwner, TimeLogApiResponse.SubmitTimeLog>) {
        return this.apiBase.configure({
            ...rest,
            method: 'POST',
            url: 'tml/v1/submission-interval/owner/for-month',
        });
    }

    public monthlySubmit(rest: IServiceHttp<TimeLogApiRequest.MonthlySubmit, TimeLogApiResponse.MonthlySubmit>) {
        return this.apiBase.configure({
            ...rest,
            method: 'POST',
            url: 'tml/v1/time-log/monthly-submit',
        });
    }

    public getDailySumByProject(rest: IServiceHttp<TimeLogApiRequest.GetDailySumByProject, TimeLogApiResponse.GetDailySumByProject>) {
        return this.apiBase.configure({
            ...rest,
            method: 'GET',
            url: 'tml/v1/time-log.dailySumByProject',
        });
    }

    public getProjectHoursSummary(rest: IServiceHttp<TimeLogApiRequest.GetProjectHoursSummary, TimeLogApiResponse.GetProjectHoursSummary>) {
        return this.apiBase.configure({
            ...rest,
            method: 'GET',
            url: 'tml/v1/time-log/project-hours-summary',
        });
    }

    public getUserTimeLogs(rest: IServiceHttp<TimeLogApiRequest.GetUserTimeLogs, TimeLogApiResponse.GetUserTimeLogs>) {
        return this.apiBase.configure({
            ...rest,
            method: 'GET',
            url: 'tml/v1/time-log/-all',
        });
    }
}

diContainer.bindAsDynamic(TimeLogApi, c => new TimeLogApi(c.get(ApiBaseClass)));
